/**
 * HelloRetailHelpers
 *
 * Centralised rendering utilities shared across ALL Hello Retail modules:
 *   hello-retail-search.js      (predictive search overlay)
 *   hello-retail-fullsearch.js  (full search page — future)
 *   helloretail-recoms.liquid   (recommendation carousels — future refactor)
 *
 * Nothing in this file knows about a specific UI module.
 * It only knows about:
 *   - The Hello Retail product data shape  (p.price, p.extraDataList, …)
 *   - The theme's CSS class conventions    (price-list, badge-list, …)
 *   - The slot contract                    (data-hr-slot names + CARD_SLOT_FILLERS)
 *
 * To adapt for a different theme: edit the HTML strings in buildPriceHtml /
 * buildBadgeHtml / buildSwatchHtml and the CARD_SLOT_FILLERS map.
 * The consuming JS files (search, fullsearch, …) ship unchanged.
 *
 * Fires `hr:helpers:ready` on document when the object is ready so that
 * inline scripts (e.g. recoms section) can safely depend on it:
 *
 *   if (window.HelloRetailHelpers) { run(); }
 *   else { document.addEventListener('hr:helpers:ready', run); }
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Formatting
  // ---------------------------------------------------------------------------

  /**
   * Format a raw price number using Shopify's moneyFormat setting.
   * Hello Retail returns prices as floats (e.g. 299.95); Shopify's
   * formatMoney expects integer cents (29995).
   */
  function fmt(amount) {
    var cents = Math.round(Number(amount) * 100);
    var format = window.themeVariables && window.themeVariables.settings.moneyFormat;
    if (window.slateCurrency && format) {
      return slateCurrency.formatMoney(cents, format);
    }
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && format) {
      return Shopify.formatMoney(cents, format);
    }
    return String(Math.round(Number(amount)));
  }

  /** Escape a value before inserting into innerHTML. */
  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ---------------------------------------------------------------------------
  // Template cloning
  // ---------------------------------------------------------------------------

  /**
   * Clone a <template> element by id and return its DocumentFragment.
   * Returns null and warns if the template is not found.
   */
  function cloneTemplate(id) {
    var tpl = document.getElementById(id);
    if (!tpl) {
      console.warn('[HR Helpers] Template not found: #' + id);
      return null;
    }
    return tpl.content.cloneNode(true);
  }

  /**
   * Generic slot filler.
   * Queries every [data-hr-slot="name"] element inside a DocumentFragment
   * or Element and calls the matching filler with (element, data).
   *
   * @param {DocumentFragment|Element} fragment
   * @param {Object} fillers  - { slotName: function(el, data) }
   * @param {Object} data     - the product (or any data) object
   */
  function fillSlots(fragment, fillers, data) {
    Object.keys(fillers).forEach(function (slotName) {
      fragment.querySelectorAll('[data-hr-slot="' + slotName + '"]').forEach(function (el) {
        fillers[slotName](el, data);
      });
    });
  }

  // ---------------------------------------------------------------------------
  // HTML builders
  // ---------------------------------------------------------------------------

  /**
   * Build price-list markup.
   * Handles regular price and on-sale (strikethrough + sale) states.
   */
  function buildPriceHtml(p) {
    if (p.isOnSale && p.oldPrice != null && p.price != null) {
      return '<dl class="price-list">'
        + '<dt class="sr-only">Regular price</dt>'
        + '<dd><s class="price-list__price price-list__price--regular">' + fmt(p.oldPrice) + '</s></dd>'
        + '<dt class="sr-only">Sale price</dt>'
        + '<dd><span class="price-list__price price-list__price--sale">' + fmt(p.price) + '</span></dd>'
        + '</dl>';
    }
    if (p.price != null) {
      return '<dl class="price-list">'
        + '<dt class="sr-only">Price</dt>'
        + '<dd><span class="price-list__price">' + fmt(p.price) + '</span></dd>'
        + '</dl>';
    }
    return '';
  }

  /**
   * Build badge-list markup.
   * Badge label text is passed in as plain strings so the caller can source
   * them from wherever makes sense (Liquid data-* attributes, JS constants, …).
   *
   * Sources:
   *   extraDataList.badges  → custom merchant badges (e.g. "Nyhed")
   *   p.inStock === false   → sold-out badge
   *   p.isOnSale            → on-sale badge
   *
   * @param {Object} p
   * @param {string} soldOutText  - translated "Sold out" label
   * @param {string} onSaleText   - translated "Sale" label
   */
  function buildBadgeHtml(p, soldOutText, onSaleText, showSoldOut) {
    var parts = [];

    var customBadges = p.extraDataList && p.extraDataList.badges;
    if (customBadges && customBadges.length) {
      customBadges.forEach(function (badge) {
        parts.push('<span class="badge badge--custom">' + escapeHtml(badge) + '</span>');
      });
    }

    if (!p.inStock && showSoldOut !== false) {
      parts.push('<sold-out-badge class="badge badge--sold-out">' + escapeHtml(soldOutText || 'Sold out') + '</sold-out-badge>');
    }

    if (p.isOnSale && p.oldPrice != null && p.price != null) {
      parts.push('<on-sale-badge class="badge badge--on-sale">' + escapeHtml(onSaleText || 'Sale') + '</on-sale-badge>');
    }

    if (!parts.length) return '';
    return '<badge-list class="badge-list badge-list--vertical">' + parts.join('') + '</badge-list>';
  }

  /**
   * Build product media — single image link or image carousel.
   *
   * Hello Retail stores all product images as JSON strings in
   * extraDataList.images (array). Falls back to p.imgUrl for a single image.
   *
   * Single image  → <a class="product-card__media"> + <img>
   * Multiple images → CSS scroll-snap carousel matching the theme's structure
   *
   * @param {Object} p         - Hello Retail product object
   * @param {string} imgClass  - CSS class string for <img> (baked in by Liquid via data-img-class)
   */
  function buildMediaHtml(p, imgClass) {
    var images = [];
    if (p.extraDataList && p.extraDataList.images && p.extraDataList.images.length) {
      images = p.extraDataList.images.map(function (imgStr) {
        try { return JSON.parse(imgStr); } catch (e) { return null; }
      }).filter(Boolean);
    }
    if (!images.length && p.imgUrl) {
      images = [{ src: p.imgUrl, alt: p.title }];
    }
    if (!images.length) return '';

    if (images.length === 1) {
      return '<a href="' + escapeHtml(p.url) + '" class="product-card__media" data-instant>'
        + '<img src="' + escapeHtml(images[0].src) + '" alt="' + escapeHtml(images[0].alt || p.title) + '" class="' + imgClass + '" loading="lazy">'
        + '</a>';
    }

    // Multiple images — CSS scroll-snap carousel (matches theme's carousel structure)
    var pId        = String(p.productNumber || p.id || '').replace(/[^a-zA-Z0-9]/g, '') || String(Date.now());
    var slideBase  = 'hrslide-' + pId;
    var lastNum    = images.length;

    var slides = images.map(function (img, i) {
      var num = i + 1;
      return '<li id="' + slideBase + '-' + num + '" tabindex="0" class="carousel__slide">'
        + '<a href="' + escapeHtml(p.url) + '" tabindex="0">'
        + '<img src="' + escapeHtml(img.src) + '" alt="' + escapeHtml(img.alt || p.title) + '" class="' + imgClass + '" loading="lazy">'
        + '</a>'
        + '<div class="carousel__snapper"></div>'
        + '<button class="carousel__control carousel__controls_prev"'
        +   ' data-slide-id="' + slideBase + '-' + (num - 1) + '"'
        +   (num === 2 ? ' data-image-index="first"' : '') + '></button>'
        + '<button class="carousel__control carousel__controls_next"'
        +   ' data-slide-id="' + slideBase + '-' + (num + 1) + '"'
        +   (num === lastNum - 1 ? ' data-image-index="last"' : '') + '></button>'
        + '</li>';
    }).join('');

    return '<div class="product-card__media" data-instant>'
      + '<div class="carousel hidden-prev" aria-label="Gallery">'
      + '<ol class="carousel__viewport">' + slides + '</ol>'
      + '</div>'
      + '</div>';
  }

  /**
   * Build VariantRobot color swatch dots.
   * Shows up to 2 colored dots + "+N" overflow label.
   * Source: extraDataList.vrColorSwatches (array of JSON strings with a .color key).
   */
  function buildSwatchHtml(p) {
    var swatches = p.extraDataList && p.extraDataList.vrColorSwatches;
    if (!swatches || swatches.length <= 1) return '';

    var shown = 0;
    var dotsHtml = swatches.reduce(function (acc, str) {
      if (shown >= 2) return acc;
      try {
        var s = JSON.parse(str);
        if (!s.color) return acc;
        shown++;
        return acc + '<span style="background-color:' + escapeHtml(s.color) + ';"></span>';
      } catch (e) { return acc; }
    }, '');

    if (!dotsHtml) return '';
    var extra = swatches.length > 2 ? '+' + (swatches.length - 2) : '';
    return '<div class="VariantRobot--Collection">' + dotsHtml + extra + '</div>';
  }

  // ---------------------------------------------------------------------------
  // CARD_SLOT_FILLERS
  //
  // Maps every data-hr-slot name in hr-product-card-tpl to a filler function.
  // This is the single source of truth for how Hello Retail product data is
  // bound to the product card template.
  //
  // To add a new slot: add the <template> markup in hello-retail-templates.liquid
  // and add a matching entry here. Nothing else needs to change.
  // ---------------------------------------------------------------------------
  var CARD_SLOT_FILLERS = {
    productMedia: function (el, p) {
      // imgClass is baked into the <template> by Liquid so JS never hard-codes aspect ratios
      var tpl      = document.getElementById('hr-product-card-tpl');
      var imgClass = (tpl && tpl.dataset.imgClass) || 'product-card__image product-card__image--primary object-cover';
      el.innerHTML = buildMediaHtml(p, imgClass);
    },

    productTitle: function (el, p) {
      el.href = p.url;
      // Strip variant suffix — "Product | Colour" → "Product"
      el.textContent = (p.title || '').split(' | ')[0];
    },

    productPrice: function (el, p) {
      el.innerHTML = buildPriceHtml(p);
    },

    productBadges: function (el, p) {
      // Translated badge label text is baked into data-* on the <template> by Liquid
      var tpl = document.getElementById('hr-product-card-tpl');
      var soldOutText = (tpl && tpl.dataset.soldOut) || 'Sold out';
      var onSaleText  = (tpl && tpl.dataset.onSale)  || 'Sale';
      var showSoldOut = !tpl || tpl.dataset.showSoldOut !== 'false';
      el.innerHTML = buildBadgeHtml(p, soldOutText, onSaleText, showSoldOut);
    },

    productSwatches: function (el, p) {
      el.innerHTML = buildSwatchHtml(p);
    }
  };

  // ---------------------------------------------------------------------------
  // Expose + signal ready
  // ---------------------------------------------------------------------------

  window.HelloRetailHelpers = {
    fmt:               fmt,
    escapeHtml:        escapeHtml,
    cloneTemplate:     cloneTemplate,
    fillSlots:         fillSlots,
    buildMediaHtml:    buildMediaHtml,
    buildPriceHtml:    buildPriceHtml,
    buildBadgeHtml:    buildBadgeHtml,
    buildSwatchHtml:   buildSwatchHtml,
    CARD_SLOT_FILLERS: CARD_SLOT_FILLERS
  };

  document.dispatchEvent(new CustomEvent('hr:helpers:ready'));
})();
